import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAdminAuthStore } from '../store/adminAuthStore';
import { useNotificationStore } from '../store/notificationStore';
import { NotificationDto } from '../api/notifications';
import { getAdminNotificationsHubUrl } from '../config/adminRuntime';

const POLL_INTERVAL_MS = 30_000;

export function useNotifications() {
  const { isAuthenticated, accessToken } = useAdminAuthStore();
  const { fetchNotifications, addNotification } = useNotificationStore();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    fetchNotifications();

    pollRef.current = setInterval(() => {
      fetchNotifications();
    }, POLL_INTERVAL_MS);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(getAdminNotificationsHubUrl(), {
        accessTokenFactory: () => localStorage.getItem('adminToken') ?? '',
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('ReceiveNotification', (notification: NotificationDto) => {
      addNotification(notification);
    });

    const startConnection = async () => {
      try {
        await connection.start();
        console.info('SignalR connected');
      } catch (err) {
        console.warn('SignalR initial connection failed:', err);
        try {
          await useAdminAuthStore.getState().refreshAccessToken();
        } catch {
          return;
        }
      }
    };

    void startConnection();
    connectionRef.current = connection;

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      connection.stop();
    };
  }, [isAuthenticated, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps
}
